import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import type { HelocLoanInfo } from '@ld.consumer-portal/models';
import {
  ENVIRONMENT,
  HelocDrawRecord,
  HelocDrawRecordSaveRequest,
  HelocDrawRecordSaveResponse,
  HelocInitiateDrawRequest,
  HelocInitiateDrawResponse,
} from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';

export type {
  HelocDrawRecord,
  HelocDrawRecordSaveRequest,
  HelocDrawRecordSaveResponse,
  HelocEligibility,
  HelocInitiateDrawRequest,
  HelocInitiateDrawResponse,
  HelocLoanInfo,
} from '@ld.consumer-portal/models';

@Injectable({
  providedIn: 'root',
})
export class HelocService {
  private readonly http = inject(HttpClient);
  private readonly env = inject(ENVIRONMENT);

  readonly lastDrawResponse = signal<HelocDrawRecordSaveResponse | null>(null);

  private get integrationBaseUrl(): string {
    return `${this.env.appSettings.melloServPaymentIntegrationUrl}/${this.env.appSettings.apiNamespace}/MelloServPaymentIntegration`;
  }

  private get drawsBaseUrl(): string {
    return `${this.env.appSettings.ServicingLINCPortalPayments}/${this.env.appSettings.apiNamespace}/ServicingLINCPortalIntegration/HelocDraws`;
  }

  async getHelocLoanInfo(loanNumber: string, encryptionValue: string): Promise<HelocLoanInfo> {
    const url = `${this.integrationBaseUrl}/HelocLoanInfo/${loanNumber}/${encryptionValue}`;
    return firstValueFrom(this.http.get<HelocLoanInfo>(url));
  }

  async getDrawRecords(loanNumber: string, status?: string): Promise<HelocDrawRecord[]> {
    const params: Record<string, string> = { loanNumber };
    if (status) {
      params['status'] = status;
    }

    return firstValueFrom(this.http.get<HelocDrawRecord[]>(this.drawsBaseUrl, { params }));
  }

  async saveDrawRecord(request: HelocDrawRecordSaveRequest): Promise<HelocDrawRecordSaveResponse> {
    return firstValueFrom(this.http.post<HelocDrawRecordSaveResponse>(this.drawsBaseUrl, request));
  }

  async initiateDrawRequest(request: HelocInitiateDrawRequest): Promise<HelocInitiateDrawResponse> {
    const url = `${this.drawsBaseUrl}/Initiate`;
    return firstValueFrom(this.http.post<HelocInitiateDrawResponse>(url, request));
  }
}
